import React from 'react';
import { Box, Icon, Typography } from '@mui/material';
import { get } from 'global/lib/lodash';
import { FlexGridCellRoot } from './styles';

/** Shared muted icon color for FlexGrid list + related pickers. */
export const listIconColor = 'grey.800';

/**
 * Muted leading icon chrome for list cells (vendors Access / Editions / Scorecards look).
 * Use with FlexGridIconLabel, or alone when only the icon wrap is needed.
 */
export const listIconSx = {
  width: 22,
  height: 22,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: listIconColor,
  '& .MuiSvgIcon-root, & .MuiIcon-root': { fontSize: 18, color: 'inherit' },
};

export const DEFAULT_LIST_HINT = 'Click on a row to configure';

/** Italic hint above row-click FlexGrid lists. Override `children` for custom copy. */
export function FlexGridListHint({ children = DEFAULT_LIST_HINT, sx, ...rest }) {
  return (
    <Typography variant="body2" color="text.secondary" {...rest} sx={{ fontStyle: 'italic', pb: 1.5, ...sx }}>
      {children}
    </Typography>
  );
}

/**
 * Icon + label row for FlexGrid list cells.
 * @param {React.ReactNode} icon
 * @param {React.ReactNode} children
 * @param {string} [title] - native tooltip; defaults to string children
 * @param {boolean} [noWrap=true]
 * @param {object} [iconSx] - merged onto listIconSx (e.g. `{ p: 1 }` in denser chrome)
 */
export function FlexGridIconLabel({ icon, children, title, noWrap = true, iconSx, sx, ...rest }) {
  const tip = title || (typeof children === 'string' ? children : undefined);
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, ...sx }}
      {...rest}
    >
      {icon != null && <Box sx={{ ...listIconSx, ...iconSx }}>{icon}</Box>}
      <Typography variant="body2" noWrap={noWrap} title={tip} sx={{ minWidth: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

const ACTION_FIELDS = new Set(['_check', '_edit', 'enabled', 'edit_config', 'blank']);

export const cellWidth = (width) => (typeof width === 'number' ? `${width}px` : width);

/** Italic secondary empty state for summary cells. */
export function EmptyPlaceholder({ children = 'None', ...rest }) {
  return (
    <Typography variant="body2" color="text.secondary" fontStyle="italic" {...rest}>
      {children}
    </Typography>
  );
}

/** Clickable column header with sort affordance (trades-style). */
export function FlexGridSortHeader({ label, direction = 'asc', onClick }) {
  return (
    <Box
      component="span"
      onClick={onClick}
      sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
    >
      {label}
      <Icon fontSize="small">{direction === 'desc' ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>
    </Box>
  );
}

/**
 * If no column sets flexGrow, assign flexGrow: 1 to the first non-action column
 * so tables fill row width by default.
 */
export function resolveColumns(columns = []) {
  if (columns.some((c) => c.flexGrow != null)) return columns;
  let assigned = false;
  return columns.map((col) => {
    if (assigned || col.noGrow || ACTION_FIELDS.has(col.field)) return col;
    assigned = true;
    return { ...col, flexGrow: 1 };
  });
}

function headerTitle(col) {
  if (col.sortable) {
    return (
      <FlexGridSortHeader label={col.title} direction={col.sortDir || 'asc'} onClick={col.onSort} />
    );
  }
  return col.title;
}

/** Header cells for a column config (supports col.sortable). */
export function renderFlexGridHeaderCells(columns = []) {
  const cols = resolveColumns(columns);
  return cols.map((col) => (
    <FlexGridCellRoot
      key={col.field}
      width={cellWidth(col.width)}
      minWidth={col.minWidth != null ? cellWidth(col.minWidth) : undefined}
      hideBelow={col.hideBelow}
      stackAt={col.stackAt}
      flexGrow={col.flexGrow}
      sx={col.cellSx}
    >
      {headerTitle(col)}
    </FlexGridCellRoot>
  ));
}

/** Body cells for one item — use inside custom renderRow accordion chrome. */
export function renderFlexGridColumns(columns = [], item, index) {
  const cols = resolveColumns(columns);
  return cols.map((col) => {
    const raw = get(item, col.field);
    const val = col.render ? col.render(item, raw, index) : raw;
    return (
      <FlexGridCellRoot
        key={col.field}
        width={cellWidth(col.width)}
        minWidth={col.minWidth != null ? cellWidth(col.minWidth) : undefined}
        hideBelow={col.hideBelow}
        stackAt={col.stackAt}
        flexGrow={col.flexGrow}
        sx={col.cellSx}
      >
        {val}
      </FlexGridCellRoot>
    );
  });
}
